import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import {
  Checkbox,
  FormLabel,
  TextInput,
  Notification,
  requiredField,
  trackEvent,
} from '@offerzen/pinata'

import { restClient } from 'config/restClient'

import ControlledHookField from '../../ControlledHookField'

import SubmitButton from './SubmitButton'
import styles from './styles'

const updateSignTermsRequest = async (name, token, companyId) => {
  const response = await restClient(`/api/company/v2/company_profiles/${companyId}/sign_terms_request`, {
    method: 'PUT',
    data: {
      company_id: companyId,
      token,
      name,
    },
  })

  return response.data
}

const validators = {
  required: requiredField('Required'),
}

const AcceptTermsForm = ({ token, companyId, onSubmitSuccess, className }) => {
  const [formError, setFormError] = useState()
  const { control, handleSubmit } = useForm()

  const { mutate: acceptTerms, isLoading } = useMutation(
    values => updateSignTermsRequest(values.name, token, companyId),
    {
      onSuccess: () => {
        trackEvent(
          'Company:T&Cs',
          {
            source: 'company terms page',
            action: 'clicked accepted terms',
          },
        )
        onSubmitSuccess()
      },
      onError: error => {
        setFormError(
          error.response.status === 401 ? 'You’re not authorised to accept these terms.' : 'Something went wrong. Please try again.',
        )
      },
    },
  )

  return (
    <form
      onSubmit={handleSubmit(acceptTerms)}
      onChange={() => formError && setFormError(null)}
      className={cx(styles.form, className)}
      aria-label="Accept terms"
    >
      <div>
        <FormLabel label="Full name" htmlFor="name" />
        <ControlledHookField
          placeholder="Enter your full name"
          className={styles.nameInput}
          control={control}
          id="name"
          name="name"
          component={TextInput}
          spellCheck="false"
          validate={validators.required}
        />
      </div>

      <ControlledHookField
        className={styles.acceptTerms}
        control={control}
        id="accept"
        name="accept"
        component={Checkbox}
        label="I have read and understood these terms and conditions and I confirm that I’m authorised to accept these terms on behalf of my company."
        validate={validators.required}
      />

      <SubmitButton isSubmitting={isLoading} className={styles.submit} />

      <If condition={formError}>
        <Notification theme="error"><p role="alert" className={styles.errorMessage}>{formError}</p></Notification>
      </If>
    </form>
  )
}

AcceptTermsForm.propTypes = {
  token: PropTypes.string,
  companyId: PropTypes.string.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  className: PropTypes.string,
}

export default AcceptTermsForm
